import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  message,
  Modal,
  Form,
  Card,
  List,
  Popconfirm,
  Spin,
  Switch,
  Select,
  Tooltip,
  Tag,
  Progress,
  DatePicker,
  InputNumber,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TagOutlined,
  PercentageOutlined,
  DollarOutlined,
  CalendarOutlined,
  TeamOutlined,
  FireOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  PoundCircleOutlined,
} from "@ant-design/icons";
import drEasyApi from "../../../Api/Customapi";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

// ─── helpers ────────────────────────────────────────────────────────────────

const statusTag = (coupon) => {
  if (coupon.isExpired)
    return (
      <Tag icon={<CloseCircleOutlined />} color="red">
        Expired
      </Tag>
    );
  if (!coupon.isActive)
    return (
      <Tag icon={<CloseCircleOutlined />} color="default">
        Inactive
      </Tag>
    );
  if (!coupon.isStarted)
    return (
      <Tag icon={<ClockCircleOutlined />} color="orange">
        Scheduled
      </Tag>
    );
  return (
    <Tag icon={<CheckCircleOutlined />} color="green">
      Active
    </Tag>
  );
};

const typeColor = (type) => (type === 1 ? "#1f6f7d" : "#FEBA17");
const typeLabel = (type) => (type === 1 ? "Percentage" : "Fixed Amount");
const typeIcon = (type) =>
  type === 1 ? (
    <PercentageOutlined style={{ fontSize: 22 }} />
  ) : (
    <PoundCircleOutlined style={{ fontSize: 22 }} />
  );

// ─── component ──────────────────────────────────────────────────────────────

const CouponsScreen = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [filterActive, setFilterActive] = useState(null);
  const [form] = Form.useForm();

  // watched form values for conditional rendering
  const watchType = Form.useWatch("type", form);
  const watchIsUnlimited = Form.useWatch("isUnlimited", form);
  const watchIsActive = Form.useWatch("isActive", form);

  useEffect(() => {
    fetchCoupons();
  }, [filterActive]);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const params = filterActive !== undefined ? { IsActive: filterActive } : {};
      const res = await drEasyApi.get("/api/Coupons/GetAll", { params });
      setCoupons(res.data || []);
    } catch (err) {
      message.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  // ── modal open/close ───────────────────────────────────────────────────────

  const handleAdd = () => {
    setEditingCoupon(null);
    form.resetFields();
    form.setFieldsValue({ type: 1, isUnlimited: true });
    setIsModalVisible(true);
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    form.setFieldsValue({
      code: coupon.code,
      type: coupon.type,
      value: coupon.value,
      maxDiscountAmount: coupon.maxDiscountAmount || null,
      isUnlimited: coupon.isUnlimited,
      usageLimit: coupon.usageLimit ?? null,
      usageLimitPerUser: coupon.usageLimitPerUser || null,
      dateRange: [dayjs(coupon.startDate), dayjs(coupon.expiryDate)],
      isActive: coupon.isActive,
    });
    setIsModalVisible(true);
  };

  // ── submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const [startDate, expiryDate] = values.dateRange;

      const payload = {
        code: values.code.trim().toUpperCase(),
        type: values.type,
        value: values.value,
        maxDiscountAmount:
          values.type === 1 ? (values.maxDiscountAmount || null) : null,  // ← null instead of 0
        isUnlimited: values.isUnlimited,
        usageLimit: values.isUnlimited ? null : values.usageLimit,        // ← null instead of 0
        usageLimitPerUser: values.usageLimitPerUser || null,              // ← null instead of 0
        startDate: startDate.toISOString(),
        expiryDate: expiryDate.toISOString(),
      };

      if (editingCoupon) {
        await drEasyApi.put("/api/Coupons/Edit", {
          couponId: editingCoupon.id,
          value: payload.value,
          maxDiscountAmount: payload.maxDiscountAmount,
          isUnlimited: payload.isUnlimited,
          usageLimit: payload.usageLimit,
          usageLimitPerUser: payload.usageLimitPerUser,
          startDate: payload.startDate,
          expiryDate: payload.expiryDate,
          isActive: values.isActive ?? editingCoupon.isActive,
        });
        message.success("Coupon updated");
      } else {
        await drEasyApi.post("/api/Coupons/Add", payload);
        message.success("Coupon added");
      }

      fetchCoupons();
      setIsModalVisible(false);
    } catch (err) {
      if (err?.response?.status === 400) {
        message.error(
          err.response.data?.errors?.[0] || "Please check your input"
        );
      } else if (err?.errorFields) {
        message.error("Please fill all required fields");
      } else {
        message.error("Failed to submit coupon");
      }
    }
  };

  // ── delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    try {
      await drEasyApi.delete("/api/Coupons/Delete", { params: { CouponId: id } });
      message.success("Coupon deleted");
      fetchCoupons();
    } catch {
      message.error("Failed to delete coupon");
    }
  };

  // ─── render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6">
      {/* ── header ── */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Coupons</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage discount coupons for your patients
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* filter */}
          <Select
            className="w-36"
            value={filterActive}
            onChange={setFilterActive}
            options={[
              { value: null, label: "All Coupons" },
              { value: true, label: "Active Only" },
              { value: false, label: "Inactive Only" },
            ]}
          />

          <Button
            type=""
            size="large"
            className="bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Coupon
          </Button>
        </div>
      </div>

      {/* ── list ── */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <Spin size="large" />
        </div>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4 }}
          dataSource={coupons}
          locale={{ emptyText: "No coupons found" }}
          renderItem={(item) => (
            <List.Item>
              <Card
                className="overflow-hidden shadow-md border border-[#298d9f73] hover:shadow-lg transition-all rounded-lg"
                cover={
                  <div
                    className="flex flex-col justify-center items-center h-32 relative"

                  >
                    <div
                      className="mb-2 rounded-full p-3"
                      style={{
                        color: typeColor(item.type),
                      }}
                    >
                      {typeIcon(item.type)}
                    </div>

                    {/* coupon code */}
                    <div
                      className="font-mono text-lg font-bold tracking-widest px-4 py-1 rounded-md"
                      style={{
                        background: `${typeColor(item.type)}15`,
                        color: typeColor(item.type),
                        border: `1.5px  ${typeColor(item.type)}60`,
                        letterSpacing: "0.15em",
                      }}
                    >
                      {item.code}
                    </div>

                    {/* status tag */}
                    <div className="absolute top-2 right-2">{statusTag(item)}</div>
                  </div>
                }
                actions={[
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(item)}
                    type="text"
                    key="edit"
                  />,
                  <Popconfirm
                    title="Are you sure to delete this coupon?"
                    onConfirm={() => handleDelete(item.id)}
                    key="delete"
                  >
                    <Button danger icon={<DeleteOutlined />} type="text" />
                  </Popconfirm>,
                ]}
              >
                <Card.Meta
                  title={
                    <div className="flex justify-between items-center">
                      <span
                        className="text-base font-bold"
                        style={{ color: typeColor(item.type) }}
                      >
                        {item.typeLabel}
                      </span>
                      <Tag
                        color={item.type === 1 ? "cyan" : "orange"}
                        className="text-xs"
                      >
                        {typeLabel(item.type)}
                      </Tag>
                    </div>
                  }
                  description={
                    <div className="space-y-2 mt-1 h-24">
                      {/* usage */}
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <TeamOutlined />
                        {item.isUnlimited ? (
                          <span>
                            <b>{item.usedCount}</b> used · Unlimited
                          </span>
                        ) : (
                          <span>
                            <b>{item.usedCount}</b> / {item.usageLimit} used
                          </span>
                        )}
                      </div>

                      {/* progress bar (only when limited) */}
                      {!item.isUnlimited && item.usageLimit > 0 && (
                        <Progress
                          percent={Math.round(item.usagePercentage)}
                          size="small"
                          strokeColor={typeColor(item.type)}
                          showInfo={false}
                        />
                      )}

                      {/* dates */}
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <CalendarOutlined />
                        {dayjs(item.startDate).format("MMM D")} →{" "}
                        {dayjs(item.expiryDate).format("MMM D, YYYY")}
                      </div>

                      {/* unique users */}
                      {item.uniqueUsersCount > 0 && (
                        <div className="text-xs text-gray-400">
                          <FireOutlined className="mr-1 text-orange-400" />
                          {item.uniqueUsersCount} unique user
                          {item.uniqueUsersCount > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
      )}

      {/* ── modal ── */}
      <Modal
        title={editingCoupon ? "Edit Coupon" : "Add Coupon"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSubmit}
            style={{ backgroundColor: "#298d9f", borderColor: "#298d9f" }}
          >
            Save
          </Button>,
        ]}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ type: 1, isUnlimited: true }}>

          {/* code (disabled on edit) */}
          <Form.Item
            name="code"
            label="Coupon Code"
            rules={[{ required: true, message: "Please enter coupon code" }]}
            extra="Will be auto-converted to uppercase"
          >
            <Input
              disabled={!!editingCoupon}
              placeholder="e.g. SUMMER20"
              prefix={<TagOutlined className="text-gray-400" />}
              style={{ fontFamily: "monospace", letterSpacing: "0.1em" }}
              onChange={(e) =>
                form.setFieldValue("code", e.target.value.toUpperCase())
              }
            />
          </Form.Item>

          {/* type (disabled on edit) */}
          <Form.Item
            name="type"
            label="Coupon Type"
            rules={[{ required: true }]}
          >
            <Select disabled={!!editingCoupon}>
              <Select.Option value={1}>
                <PercentageOutlined className="mr-1" /> Percentage
              </Select.Option>
              <Select.Option value={2}>
                <DollarOutlined className="mr-1" /> Fixed Amount
              </Select.Option>
            </Select>
          </Form.Item>

          {/* value */}
          <Form.Item
            name="value"
            label={watchType === 1 ? "Discount Percentage" : "Discount Amount"}
            rules={[{ required: true, message: "Please enter discount value" }]}
          >
            <InputNumber
              min={1}
              max={watchType === 1 ? 100 : undefined}
              step={1}
              className="w-full"
              placeholder={watchType === 1 ? "e.g. 20" : "e.g. 50"}
              addonAfter={watchType === 1 ? "%" : "EGP"}
            />
          </Form.Item>

          {/* maxDiscountAmount – only for percentage coupons */}
          {watchType === 1 && (
            <Form.Item
              name="maxDiscountAmount"
              label="Max Discount Amount (optional)"
              extra="Cap the maximum discount given for percentage coupons"
            >
              <InputNumber
                min={0}
                step={1}
                className="w-full"
                placeholder="e.g. 200"
                addonAfter="EGP"
              />
            </Form.Item>
          )}

          <Divider className="!my-3" />

          {/* isUnlimited */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-gray-700">
              Unlimited Usage
            </div>
            <Form.Item name="isUnlimited" valuePropName="checked" className="!mb-0">
              <Switch
                checkedChildren="On"
                unCheckedChildren="Off"
                style={{
                  backgroundColor: watchIsUnlimited ? "#298d9f" : undefined,
                }}
              />
            </Form.Item>
          </div>

          {/* usageLimit – hidden when unlimited */}
          {!watchIsUnlimited && (
            <Form.Item
              name="usageLimit"
              label="Total Usage Limit"
              rules={[
                {
                  required: true,
                  message: "Please enter usage limit",
                },
              ]}
            >
              <InputNumber min={1} step={1} className="w-full" placeholder="e.g. 100" />
            </Form.Item>
          )}

          {/* usageLimitPerUser */}
          <Form.Item
            name="usageLimitPerUser"
            label="Usage Limit Per User (optional)"
            extra="Leave empty for unlimited per user"
          >
            <InputNumber
              min={1}
              step={1}
              className="w-full"
              placeholder="e.g. 1"
            />
          </Form.Item>

          {/* isActive – only shown when editing */}
          {editingCoupon && (
            <>
              <Divider className="!my-3" />
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-gray-700">
                  Active Status
                </div>
                <Form.Item name="isActive" valuePropName="checked" className="!mb-0">
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    style={{
                      backgroundColor: watchIsActive ? "#46a049" : "#9e9595",
                    }}
                  />
                </Form.Item>
              </div>
            </>
          )}

          <Divider className="!my-3" />

          {/* date range */}
          <Form.Item
            name="dateRange"
            label="Validity Period"
            rules={[
              { required: true, message: "Please select start and expiry date" },
              {
                validator: (_, value) => {
                  if (!value || !value[0] || !value[1]) return Promise.resolve();
                  if (value[1].isBefore(value[0])) {
                    return Promise.reject("Expiry date must be after start date");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <RangePicker
              className="w-full"
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CouponsScreen;